import { Container, Graphics, Text } from "pixi.js";
import type { Room } from "colyseus.js";
import { getStateCallbacks } from "colyseus.js";
import type { Game } from "@engine/core/Game";
import type { Scene } from "@app/scenes/SceneManager";
import {
  AnimationSystem,
  createAnimatedNpc,
  createAnimatedPlayer,
  createEcsWorld,
  destroyEntity,
  type AnimSpec,
  type EcsWorld,
  type Entity,
  MovementSystem,
  RenderSyncSystem,
} from "@engine/ecs";
import type { GameClient } from "@game/services";
import type { GameSession } from "@game/game-context";
import type {
  Realm,
  WorldZoneState,
  ZonePlayer,
  ZoneMob,
  ZoneBossLite,
  ZoneNpcLite,
  ZoneExitLite,
  WorldEncounterEvent,
  WorldNpcInteractEvent,
  WorldTeleportEvent,
} from "@game/types";

const ATLAS = "character";
const ATLAS_URL = "/atlases/character.json";

const REALM_BG: Record<Realm, number> = {
  real_world: 0x1d2735,
  digital_world: 0x122035,
  spiral_mountain: 0x2b1c2c,
  void: 0x080810,
};

const CHARACTER_ANIMS: AnimSpec[] = [
  {
    state: "idle",
    byDir: { down: "char-idle-down", left: "char-idle-left", right: "char-idle-right", up: "char-idle-up" },
    fps: 4,
  },
  {
    state: "walk",
    byDir: { down: "char-walk-down", left: "char-walk-left", right: "char-walk-right", up: "char-walk-up" },
    fps: 8,
  },
];

export interface WorldSceneCallbacks {
  onEncounter: (e: WorldEncounterEvent) => void;
  onTeleport: (e: WorldTeleportEvent) => void;
  onNpcInteract: (e: WorldNpcInteractEvent) => void;
  onError: (msg: string) => void;
}

export interface WorldSceneOpts {
  client: GameClient;
  session: GameSession;
  callbacks: WorldSceneCallbacks;
}

const DIR_TO_HEADING = [Math.PI / 2, Math.PI, 0, -Math.PI / 2]; // down, left, right, up
/** Hit radius around a marker for click-to-interact (world px). */
const MARKER_HIT_R = 28;
/** Range to send `interact` via key. NPCs use ~96, exits use exit.interactRadius. */
const KEY_INTERACT_R_DEFAULT = 96;

interface InteractMarker {
  kind: "npc" | "exit" | "mob" | "boss";
  id: string;
  x: number;
  y: number;
  hitR: number;
}

/**
 * Bridges the Colyseus `world_zone` room into the engine's ECS world.
 *
 * - Players from `state.players` → ECS animated entities
 * - Mobs from `state.mobs` → ECS animated entities (tinted by behavior)
 * - Bosses / NPCs / exits → markers on the overlay layer (clickable)
 * - Backdrop is sized from `state.width/height/tileSize` and re-drawn the
 *   first time those values arrive (Colyseus state is hydrated async).
 *
 * Movement is sampled at 20 Hz with optimistic prediction; server-authoritative
 * position reconciles softly (12 px snap threshold).
 *
 * Interact:
 *  • Key `E` (or Space) → picks the closest NPC/exit/boss within 96 px from
 *    the player and sends `interact` (or POST /world/teleport for exits).
 *  • Click on a marker (NPC dot, exit ring, boss dot) → same. Otherwise click
 *    sends `target` (move-to-point).
 */
export class WorldClientScene implements Scene {
  readonly name = "world";
  private game: Game | undefined;
  private ecs: EcsWorld | undefined;
  private room: Room<WorldZoneState> | undefined;
  private addedSystems: unknown[] = [];
  private rootBg: Container | undefined;
  private overlay: Container | undefined;
  private playerEntities = new Map<string, Entity>();
  private mobEntities = new Map<string, Entity>();
  private myCharId = "";
  private myEntity: Entity | undefined;
  private sendAcc = 0;
  private lastSentDx = 0;
  private lastSentDy = 0;
  private detachListeners: (() => void) | undefined;
  private backdropDrawnFor = "";
  private markers: InteractMarker[] = [];
  /** Cached zone bounds (set when first state diff arrives). */
  private worldW = 0;
  private worldH = 0;

  constructor(private readonly opts: WorldSceneOpts) {}

  async enter(game: Game): Promise<void> {
    this.game = game;
    this.myCharId = this.opts.session.me.id;

    await game.atlases.load(ATLAS, ATLAS_URL);

    const ecs = createEcsWorld();
    this.ecs = ecs;

    const tok = this.opts.client.auth.current()?.token;
    if (!tok) throw new Error("not authenticated");
    const room = await this.opts.client.colyseus.joinOrCreate<WorldZoneState>(
      "world_zone",
      { token: tok, zoneId: this.opts.session.position.zone },
      {
        onReconnect: (r) => this.attachRoomHandlers(r as Room<WorldZoneState>),
        onLost: (code) => this.opts.callbacks.onError(`Disconnected (code ${code}).`),
      },
    );
    this.room = room;
    this.attachRoomHandlers(room);

    this.rootBg = new Container({ label: "world-bg" });
    game.layers.attach("ground", this.rootBg);
    this.overlay = new Container({ label: "world-overlay" });
    game.layers.attach("aboveObject", this.overlay);

    this.maybeRedrawBackdrop();
    this.refreshOverlays();

    const move = new MovementSystem(ecs);
    const sync = new RenderSyncSystem(ecs);
    const anim = new AnimationSystem(ecs);
    game.addUpdateSystem(move);
    game.addUpdateSystem(anim);
    game.addRenderSystem(sync);

    const predictPump = { update: (dt: number) => this.tickPredict(dt) };
    game.addUpdateSystem(predictPump);
    this.addedSystems = [move, anim, sync, predictPump];

    game.perf.setEntityProvider(() => [...ecs.entities].length);
    game.inspector.setEntityProvider(() => ecs.entities);

    game.camera.setZoom(1.5, true);

    const canvas = game.app.canvas;
    const onClick = (ev: MouseEvent): void => {
      if (!this.room) return;
      const rect = canvas.getBoundingClientRect();
      const sx = ev.clientX - rect.left;
      const sy = ev.clientY - rect.top;
      const w = game.camera.screenToWorld({ x: sx, y: sy });
      const hit = this.pickMarker(w.x, w.y, MARKER_HIT_R);
      if (hit) {
        this.sendInteract(hit);
        return;
      }
      this.room.send("target", { x: w.x, y: w.y });
    };
    canvas.addEventListener("click", onClick);

    const onKey = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.code !== "KeyE" && e.code !== "Space") return;
      const me = this.myEntity;
      if (!me?.position) return;
      const hit = this.pickMarker(me.position.x, me.position.y, KEY_INTERACT_R_DEFAULT);
      if (hit) {
        e.preventDefault();
        this.sendInteract(hit);
      }
    };
    window.addEventListener("keydown", onKey);

    this.detachListeners = (): void => {
      canvas.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }

  exit(game: Game): void {
    this.detachListeners?.();
    this.detachListeners = undefined;
    if (this.room) {
      this.opts.client.colyseus.forget("world_zone");
      this.room.leave();
      this.room = undefined;
    }
    for (const s of this.addedSystems) {
      const sys = s as { update?: (dt: number) => void; render?: (a: number) => void };
      if (sys.update) game.removeUpdateSystem(sys as { update: (dt: number) => void });
      if (sys.render) game.removeRenderSystem(sys as { render: (a: number) => void });
    }
    this.addedSystems.length = 0;
    if (this.overlay) {
      this.overlay.destroy({ children: true });
      this.overlay = undefined;
    }
    if (this.rootBg) {
      this.rootBg.destroy({ children: true });
      this.rootBg = undefined;
    }
    if (this.ecs) {
      for (const e of [...this.ecs.entities]) destroyEntity(this.ecs, e);
      this.ecs = undefined;
    }
    this.playerEntities.clear();
    this.mobEntities.clear();
    this.myEntity = undefined;
    this.markers = [];
    game.perf.clearEntityProvider();
    game.inspector.clearEntityProvider();
    game.camera.unfollow();
  }

  // ---- Colyseus → ECS bridge --------------------------------------

  private attachRoomHandlers(room: Room<WorldZoneState>): void {
    this.room = room;
    const $ = getStateCallbacks(room);
    if (!this.ecs) return;

    $(room.state).players.onAdd((p, cid) => this.upsertPlayer(cid, p));
    $(room.state).players.onChange((p, cid) => this.upsertPlayer(cid, p));
    $(room.state).players.onRemove((_, cid) => this.removePlayer(cid));

    $(room.state).mobs.onAdd((m, mid) => this.upsertMob(mid, m));
    $(room.state).mobs.onChange((m, mid) => this.upsertMob(mid, m));
    $(room.state).mobs.onRemove((_, mid) => this.removeMob(mid));

    room.onStateChange(() => {
      this.maybeRedrawBackdrop();
      this.refreshOverlays();
    });

    room.onMessage("encounter", (e: WorldEncounterEvent) => this.opts.callbacks.onEncounter(e));
    room.onMessage("npc_interact", (e: WorldNpcInteractEvent) => this.opts.callbacks.onNpcInteract(e));
    room.onMessage("teleport", (e: WorldTeleportEvent) => this.opts.callbacks.onTeleport(e));
    room.onMessage("interact_error", (e: { reason: string }) =>
      this.opts.callbacks.onError(`Interact: ${e.reason}`),
    );
  }

  /** Re-draw the grid backdrop only when zone dims change. */
  private maybeRedrawBackdrop(): void {
    const room = this.room;
    const bg = this.rootBg;
    if (!room || !bg) return;
    const w = (room.state?.width as number) ?? 0;
    const h = (room.state?.height as number) ?? 0;
    const tile = (room.state?.tileSize as number) ?? 32;
    const zoneId = (room.state?.zoneId as string) ?? "";
    if (w <= 0 || h <= 0) return;
    const key = `${zoneId}:${w}x${h}:${tile}`;
    if (key === this.backdropDrawnFor) return;
    this.backdropDrawnFor = key;

    bg.removeChildren().forEach((c) => c.destroy({ children: true }));
    const realm: Realm = (room.state?.realm as Realm) ?? "digital_world";
    const fill = REALM_BG[realm] ?? 0x121b34;
    const grid = new Graphics();
    grid.rect(0, 0, w, h).fill({ color: fill, alpha: 0.4 });
    for (let x = 0; x <= w; x += tile) grid.moveTo(x, 0).lineTo(x, h);
    for (let y = 0; y <= h; y += tile) grid.moveTo(0, y).lineTo(w, y);
    grid.stroke({ color: 0x1f2733, width: 1, alpha: 0.6 });
    grid.rect(0, 0, w, h).stroke({ color: 0x4cb1ff, width: 2, alpha: 0.6 });
    bg.addChild(grid);

    // Cache bounds so local prediction can clamp the player inside the zone.
    this.worldW = w;
    this.worldH = h;
  }

  private upsertPlayer(characterId: string, p: ZonePlayer): void {
    const ecs = this.ecs;
    const game = this.game;
    if (!ecs || !game) return;
    const isMe = characterId === this.myCharId;

    let e = this.playerEntities.get(characterId);
    if (!e) {
      e = isMe
        ? createAnimatedPlayer(ecs, game, {
            position: { x: p.x, y: p.y },
            displayName: p.name,
            atlas: ATLAS,
            anims: CHARACTER_ANIMS,
          })
        : createAnimatedNpc(ecs, game, {
            position: { x: p.x, y: p.y },
            displayName: p.name,
            atlas: ATLAS,
            anims: CHARACTER_ANIMS,
            tint: hashTint(characterId),
          });
      this.playerEntities.set(characterId, e);
      if (isMe) {
        this.myEntity = e;
        if (e.position) game.camera.follow(e.position, { lerp: 14 });
      }
    }
    if (e.position) {
      if (isMe) {
        const dx = p.x - e.position.x;
        const dy = p.y - e.position.y;
        if (Math.hypot(dx, dy) > 12) {
          e.position.x = p.x;
          e.position.y = p.y;
        }
      } else {
        e.position.x = p.x;
        e.position.y = p.y;
      }
    }
    if (e.velocity) {
      e.velocity.x = p.vx;
      e.velocity.y = p.vy;
    }
    e.heading = DIR_TO_HEADING[p.dir] ?? Math.PI / 2;
    if (e.animated) e.animated.state = p.anim === "walk" ? "walk" : "idle";
    e.displayName = p.name;
  }

  private removePlayer(characterId: string): void {
    const ecs = this.ecs;
    if (!ecs) return;
    const e = this.playerEntities.get(characterId);
    if (!e) return;
    destroyEntity(ecs, e);
    this.playerEntities.delete(characterId);
    if (characterId === this.myCharId) this.myEntity = undefined;
  }

  private upsertMob(mobId: string, m: ZoneMob): void {
    const ecs = this.ecs;
    const game = this.game;
    if (!ecs || !game) return;
    let e = this.mobEntities.get(mobId);
    if (!e) {
      e = createAnimatedNpc(ecs, game, {
        position: { x: m.x, y: m.y },
        displayName: m.speciesId,
        atlas: ATLAS,
        anims: CHARACTER_ANIMS,
        tint: hashTint(m.speciesId, m.behavior === "aggressive" ? 0xff8888 : undefined),
      });
      this.mobEntities.set(mobId, e);
    }
    if (e.position) {
      e.position.x = m.x;
      e.position.y = m.y;
    }
    if (e.animated) e.animated.state = m.anim === "walk" ? "walk" : "idle";
  }

  private removeMob(mobId: string): void {
    const ecs = this.ecs;
    if (!ecs) return;
    const e = this.mobEntities.get(mobId);
    if (!e) return;
    destroyEntity(ecs, e);
    this.mobEntities.delete(mobId);
  }

  /** Re-paint static-ish entities (npcs, exits, bosses) and refresh marker hit-list. */
  private refreshOverlays(): void {
    const room = this.room;
    const overlay = this.overlay;
    if (!room || !overlay) return;
    overlay.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.markers = [];

    for (const npc of (room.state.npcs ?? []) as ZoneNpcLite[]) {
      drawNpcMarker(overlay, npc);
      this.markers.push({ kind: "npc", id: npc.id, x: npc.x, y: npc.y, hitR: 96 });
    }
    for (const exit of (room.state.exits ?? []) as ZoneExitLite[]) {
      drawExitMarker(overlay, exit);
      this.markers.push({
        kind: "exit",
        id: exit.label,
        x: exit.x,
        y: exit.y,
        hitR: Math.max(MARKER_HIT_R, exit.interactRadius ?? 0),
      });
    }
    for (const b of (room.state.bosses ?? []) as ZoneBossLite[]) {
      drawBossMarker(overlay, b);
      this.markers.push({ kind: "boss", id: b.spawnId, x: b.x, y: b.y, hitR: 36 });
    }
  }

  private pickMarker(wx: number, wy: number, defaultR: number): InteractMarker | null {
    let best: InteractMarker | null = null;
    let bestD2 = Infinity;
    for (const m of this.markers) {
      const r = m.hitR > 0 ? m.hitR : defaultR;
      const dx = m.x - wx;
      const dy = m.y - wy;
      const d2 = dx * dx + dy * dy;
      if (d2 > r * r) continue;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = m;
      }
    }
    return best;
  }

  private sendInteract(m: InteractMarker): void {
    if (!this.room) return;
    // Per docs/COLYSEUS.md: interact accepts kind in
    //   "npc" | "exit" | "mob" | "boss"
    // and the server replies with `teleport` for exits, `npc_interact` for
    // NPCs, etc. No REST teleport call needed.
    this.room.send("interact", { kind: m.kind, id: m.id });
  }

  // ---- Movement: continuous local prediction + throttled send -------
  //
  // The previous "tickInput at 20 Hz" version moved the local player only
  // every 50 ms, which made movement look like a step function (stutter).
  // Now we:
  //   1. Move the local entity EVERY FRAME by SPEED * dt, in the direction
  //      currently held on the keyboard. Smooth at any FPS.
  //   2. Clamp to [0, w] × [0, h] so the player never leaves the visible
  //      zone bounds (server reconcile is then a tiny snap, if any).
  //   3. Send `input`/`stop` only at 20 Hz (TICK_MS) — and only when
  //      direction changed or the throttle timer elapsed.
  // The reconcile in `upsertPlayer` already smooths small drift (12 px
  // threshold), so the visible jitter goes from "ratchet" to "barely there".

  private tickPredict(dt: number): void {
    const room = this.room;
    const game = this.game;
    if (!room || !game) return;
    const { input, actions } = game;
    const dx = actions.axis(input, "move-left", "move-right");
    const dy = actions.axis(input, "move-up", "move-down");

    // 1) Local prediction every frame
    const e = this.myEntity;
    const SPEED = 120;
    if (e?.position) {
      const len = Math.hypot(dx, dy) || 1;
      const moving = dx !== 0 || dy !== 0;
      if (moving) {
        e.position.x += (dx / len) * SPEED * dt;
        e.position.y += (dy / len) * SPEED * dt;
        // Clamp to zone bounds so we don't drift past the wall visually
        if (this.worldW > 0) {
          const margin = 4;
          if (e.position.x < margin) e.position.x = margin;
          else if (e.position.x > this.worldW - margin) e.position.x = this.worldW - margin;
        }
        if (this.worldH > 0) {
          const margin = 4;
          if (e.position.y < margin) e.position.y = margin;
          else if (e.position.y > this.worldH - margin) e.position.y = this.worldH - margin;
        }
        if (e.velocity) {
          e.velocity.x = (dx / len) * SPEED;
          e.velocity.y = (dy / len) * SPEED;
        }
        if (e.animated) e.animated.state = "walk";
        if (typeof e.heading === "number") e.heading = Math.atan2(dy || 0, dx || 0);
      } else {
        if (e.velocity) {
          e.velocity.x = 0;
          e.velocity.y = 0;
        }
        if (e.animated) e.animated.state = "idle";
      }
    }

    // 2) Throttled send to server (20 Hz)
    this.sendAcc += dt;
    const dirChanged = dx !== this.lastSentDx || dy !== this.lastSentDy;
    if (this.sendAcc >= 0.05 || dirChanged) {
      this.sendAcc = 0;
      this.lastSentDx = dx;
      this.lastSentDy = dy;
      if (dx !== 0 || dy !== 0) room.send("input", { dx, dy });
      else room.send("stop", {});
    }
  }
}

// ---- markers --------------------------------------------------------

function drawNpcMarker(parent: Container, npc: ZoneNpcLite): void {
  const g = new Graphics();
  g.circle(0, 0, 10).fill({ color: 0xfacc15, alpha: 0.85 });
  g.circle(0, 0, 10).stroke({ color: 0x000000, width: 1.5, alpha: 0.6 });
  g.position.set(npc.x, npc.y);
  const label = new Text({
    text: `! ${npc.name}`,
    style: { fontFamily: "ui-monospace, monospace", fontSize: 11, fill: 0xfacc15 },
  });
  label.anchor.set(0.5, 1);
  label.position.set(npc.x, npc.y - 12);
  parent.addChild(g);
  parent.addChild(label);
}

function drawExitMarker(parent: Container, exit: ZoneExitLite): void {
  const g = new Graphics();
  g.circle(0, 0, 14).stroke({ color: 0x4cb1ff, width: 2, alpha: 0.8 });
  g.circle(0, 0, 5).fill({ color: 0x4cb1ff, alpha: 0.6 });
  g.position.set(exit.x, exit.y);
  const label = new Text({
    text: `→ ${exit.label}`,
    style: { fontFamily: "ui-monospace, monospace", fontSize: 11, fill: 0x4cb1ff },
  });
  label.anchor.set(0.5, 1);
  label.position.set(exit.x, exit.y - 18);
  parent.addChild(g);
  parent.addChild(label);
}

function drawBossMarker(parent: Container, b: ZoneBossLite): void {
  const g = new Graphics();
  g.circle(0, 0, 18).fill({ color: 0xff4444, alpha: 0.85 });
  g.circle(0, 0, 18).stroke({ color: 0x000000, width: 2, alpha: 0.7 });
  g.position.set(b.x, b.y);
  const label = new Text({
    text: `☠ ${b.bossId}  Lv ${b.level}`,
    style: { fontFamily: "ui-monospace, monospace", fontSize: 11, fill: 0xff8888 },
  });
  label.anchor.set(0.5, 1);
  label.position.set(b.x, b.y - 22);
  parent.addChild(g);
  parent.addChild(label);
}

function hashTint(s: string, base = 0xb0c0ff): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  const r = ((base >> 16) & 0xff) ^ ((h >> 0) & 0x3f);
  const g = ((base >> 8) & 0xff) ^ ((h >> 6) & 0x3f);
  const b = (base & 0xff) ^ ((h >> 12) & 0x3f);
  return (r << 16) | (g << 8) | b;
}
